import axios from 'axios';

// Use relative URLs when served through proxy, or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * API client for backend communication.
 *
 * Authentication uses the Shopify gold-standard pattern: a fresh App Bridge
 * session token is fetched before every backend request and exchanged for an
 * app JWT via POST /auth/token. A short-lived cache (50 s) and promise
 * deduplication ensure concurrent requests share a single exchange call.
 */
class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Legacy manual token (used during initial bootstrap before provider is set)
    this.token = null;

    // Per-request token provider - injected from AuthProvider
    this.sessionTokenProvider = null;
    this.shop = null;

    // Deduplication + caching for token exchange
    this._pendingTokenExchange = null;
    this._cachedAppToken = null;
    this._cachedAppTokenExpiry = 0;

    // Promise that resolves once a session-token provider has been wired up
    // by AuthProvider. The interceptor awaits this on every request so a
    // request issued before AuthProvider's bootstrap effect runs blocks
    // instead of going out unauthenticated. Fixes the "burst of 401s on
    // first paint" problem after Shopify-approval redirects, where child
    // effects (StoreProvider cold-start, Billing mount) race ahead of the
    // parent AuthProvider effect that sets the provider.
    this._providerReady = new Promise((resolve) => {
      this._resolveProviderReady = resolve;
    });

    // Request interceptor - async; fetches a fresh app JWT before each request
    this.client.interceptors.request.use(async (config) => {
      if (config._skipAuth) return config;
      if (!this.sessionTokenProvider) {
        // Wait at most 5s for AuthProvider to wire up the provider.
        await Promise.race([
          this._providerReady,
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
      }
      if (this.sessionTokenProvider) {
        const appToken = await this._getAppToken();
        config.headers.Authorization = `Bearer ${appToken}`;
      } else if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Response interceptor for error handling. On 401, transparently re-exchange
    // the session token and retry the request once. This recovers from stale
    // cached JWTs after long off-app excursions (e.g. the Shopify billing
    // approval page) where the cached app token outlives its usefulness or
    // the singleton's provider state was racy with the bootstrap. Without
    // this, every request in a parallel batch (refreshData) fails 401 and the
    // UI is stuck on stale data until the merchant manually reloads.
    this.client.interceptors.response.use(
      (response) => {
        let data = response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            // Not JSON, return as-is
          }
        }
        return data;
      },
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        if (
          status === 401 &&
          original &&
          !original._retried &&
          this.sessionTokenProvider
        ) {
          original._retried = true;
          // Invalidate the cached app token, but DO NOT touch
          // _pendingTokenExchange - parallel 401 retries should pile onto
          // a single in-flight exchange instead of each kicking off their
          // own /auth/token call.
          this._cachedAppToken = null;
          this._cachedAppTokenExpiry = 0;
          try {
            const fresh = await this._getAppToken();
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${fresh}`;
            return this.client.request(original);
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // --- Token management (legacy, used during initial bootstrap) ---

  setToken(token) {
    this.token = token;
  }

  // --- Per-request token provider ---

  setSessionTokenProvider(fn, shop) {
    this.sessionTokenProvider = fn;
    this.shop = shop;
    if (fn && this._resolveProviderReady) {
      this._resolveProviderReady();
      // Replace with a fresh resolved promise so future awaits are no-ops.
      this._providerReady = Promise.resolve();
    }
    if (!fn) {
      // Provider was nulled (logout). Future requests will block again.
      this._providerReady = new Promise((resolve) => {
        this._resolveProviderReady = resolve;
      });
    }
  }

  primeTokenCache(token) {
    this._cachedAppToken = token;
    this._cachedAppTokenExpiry = Date.now() + 50_000;
    // Once a token is in the cache, the API is fully usable. Resolve the
    // readiness gate so any request blocked in the interceptor proceeds -
    // and so subsequent requests skip the gate entirely.
    if (this._resolveProviderReady) {
      this._resolveProviderReady();
      this._providerReady = Promise.resolve();
    }
  }

  clearTokenCache() {
    this._cachedAppToken = null;
    this._cachedAppTokenExpiry = 0;
    this._pendingTokenExchange = null;
  }

  // Public hook to force a token exchange upfront. Useful at known
  // re-entry points (e.g. landing back on /app/billing after the Shopify
  // approval redirect) where we want /auth/token to complete before any
  // data fetches go out, instead of relying on the 401-retry path.
  async ensureFreshToken() {
    if (!this.sessionTokenProvider) return null;
    this._cachedAppToken = null;
    this._cachedAppTokenExpiry = 0;
    try {
      return await this._getAppToken();
    } catch {
      return null;
    }
  }

  async _getAppToken() {
    // Return cached token if still fresh
    if (this._cachedAppToken && Date.now() < this._cachedAppTokenExpiry) {
      return this._cachedAppToken;
    }

    // Deduplicate: piggyback on an in-flight exchange
    if (this._pendingTokenExchange) {
      return this._pendingTokenExchange;
    }

    // Start a new exchange
    this._pendingTokenExchange = this._exchangeToken();
    try {
      return await this._pendingTokenExchange;
    } finally {
      this._pendingTokenExchange = null;
    }
  }

  async _exchangeToken() {
    try {
      const sessionToken = await this.sessionTokenProvider();

      // Use raw axios (not this.client) to avoid interceptor recursion
      const response = await axios.post(`${API_BASE_URL}/auth/token`, {
        shop: this.shop,
        session_token: sessionToken,
      });

      const data = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;

      this._cachedAppToken = data.token;
      this._cachedAppTokenExpiry = Date.now() + 50_000; // 50 s cache
      return data.token;
    } catch (error) {
      // Clear stale cache on auth failures so next request retries cleanly
      if (error.response?.status === 401 || error.response?.status === 404) {
        this._cachedAppToken = null;
        this._cachedAppTokenExpiry = 0;
      }
      throw error;
    }
  }

  // --- HTTP helpers ---

  async get(url, params = {}) {
    return this.client.get(url, { params });
  }

  async post(url, data = {}) {
    return this.client.post(url, data);
  }

  async put(url, data = {}) {
    return this.client.put(url, data);
  }

  async delete(url) {
    return this.client.delete(url);
  }
}

export const api = new ApiClient();

/**
 * Helper functions for specific API endpoints
 */
export const templatesApi = {
  getAll: () => api.get('/api/templates'),
  get: (id) => api.get(`/api/templates/${id}`),
};

export const analyticsApi = {
  getBarAnalytics: (barId, start, end) =>
    api.get(`/api/analytics/bar/${barId}`, { start, end }),
  getSummary: (start, end) =>
    api.get('/api/analytics/summary', { start, end }),
};

export const billingApi = {
  getStatus: () => api.get('/billing/status'),
  getPlans: () => api.get('/billing/plans'),
  subscribe: (returnUrl) => api.post('/billing/subscribe', { return_url: returnUrl }),
  cancel: (keepBarId) => api.post('/billing/cancel', keepBarId ? { keep_bar_id: keepBarId } : {}),
  preCancel: () => api.get('/billing/pre-cancel'),
  sync: () => api.post('/billing/sync'),
};

export const supportApi = {
  send: (subject, message) => api.post('/api/support', { subject, message }),
  getTickets: () => api.get('/api/support/tickets'),
};

export const marketsApi = {
  getAll: () => api.get('/api/markets'),
};

export const experimentApi = {
  getAll: () => api.get('/api/experiments'),
  get: (id) => api.get(`/api/experiments/${id}`),
  create: (data) => api.post('/api/experiments', data),
  delete: (id) => api.delete(`/api/experiments/${id}`),
  start: (id) => api.post(`/api/experiments/${id}/start`),
  stop: (id) => api.post(`/api/experiments/${id}/stop`),
  getResults: (id, start, end) => api.get(`/api/experiments/${id}/results`, { start, end }),
};
